import type {
  ReportCategory,
  ReportPetitionSummary,
  ReportReceipt,
  ReportSignature,
  ReportSummary,
} from "@hydeck/mdt-contracts";
import { createEffect, createSignal, For, Show } from "solid-js";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";

export function ReportWorkflowPanel(props: { report: ReportSummary; category?: ReportCategory; onReload: () => Promise<void> }) {
  const [receipts, setReceipts] = createSignal<ReportReceipt[]>([]);
  const [signatures, setSignatures] = createSignal<ReportSignature[]>([]);
  const [petition, setPetition] = createSignal<ReportPetitionSummary>({ count: 0, userSigned: false });
  const [busy, setBusy] = createSignal(false);
  const [signatureType, setSignatureType] = createSignal("cop");
  const token = () => currentUser()?.accessToken;
  const base = () => `/reports/${props.report.reportCategoryId}/${props.report.id}`;
  const hasReceipts = () => props.category?.hasReceipts !== false;
  const hasPetition = () => props.category?.hasPetition === true;
  const signatureTypes = () => Object.entries(props.category?.hasSignatures ?? { cop: true })
    .filter(([, enabled]) => enabled)
    .map(([type]) => type);
  const hasSignatures = () => signatureTypes().length > 0;

  const load = async () => {
    const [readers, signed, petitionState] = await Promise.all([
      hasReceipts() ? apiRequest<ReportReceipt[]>(`${base()}/receipts`, { token: token() }) : Promise.resolve([]),
      hasSignatures() ? apiRequest<ReportSignature[]>(`${base()}/signatures`, { token: token() }) : Promise.resolve([]),
      hasPetition() ? apiRequest<ReportPetitionSummary>(`${base()}/petitions`, { token: token() }) : Promise.resolve({ count: 0, userSigned: false }),
    ]);
    setReceipts(readers);
    setSignatures(signed);
    setPetition(petitionState);
    if (signatureTypes().length > 0 && !signatureTypes().includes(signatureType())) setSignatureType(signatureTypes()[0]!);
  };

  createEffect(() => {
    props.report.id;
    void load().catch(() => undefined);
  });

  const markRead = async () => {
    setBusy(true);
    try {
      await apiRequest(`${base()}/receipts`, { method: "POST", token: token() });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const sign = async () => {
    if (!props.report.isLocked) return;
    setBusy(true);
    try {
      await apiRequest(`${base()}/signatures`, {
        method: "POST",
        token: token(),
        body: { signatureType: signatureType() },
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const petitionFor = async () => {
    if (!props.report.isLocked || petition().userSigned) return;
    setBusy(true);
    try {
      await apiRequest(`${base()}/petitions`, { method: "POST", token: token() });
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="flex flex-col gap-2 bg-[rgba(255,255,255,.04)] p-3">
      <Show when={hasReceipts()}>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="roboto500">Report workflow</p>
        <Show when={!receipts().some((item) => item.userId === currentUser()?.id)}>
          <button class="bg-green-gradient px-3 py-1 text-xs" disabled={busy()} onClick={() => void markRead()}>
            Mark as read
          </button>
        </Show>
        <span class="text-xs text-white-55">Read by {receipts().length}</span>
      </div>
      </Show>
      <Show when={hasSignatures()}>
      <div class="flex flex-col gap-2 bg-gray-gradient p-2">
        <p class="text-sm">Signatures {props.report.isLocked ? "(document locked)" : "(lock before signing)"}</p>
        <div class="flex flex-wrap gap-2">
          <For each={signatures()}>
            {(signature) => <span class="bg-white-11 px-2 py-1 text-xs">{signature.signatureType}: {signature.firstName} {signature.lastName}</span>}
          </For>
        </div>
        <Show when={props.report.isLocked && hasPermission("reports.update")}>
          <div class="flex gap-2">
            <select class="bg-white-11 px-2 text-xs text-white" value={signatureType()} onChange={(event) => setSignatureType(event.currentTarget.value)}>
              <For each={signatureTypes()}>{(type) => <option value={type}>{signatureLabel(type)}</option>}</For>
            </select>
            <button class="bg-green-gradient px-3 py-1 text-xs" disabled={busy()} onClick={() => void sign()}>Sign</button>
          </div>
        </Show>
      </div>
      </Show>
      <Show when={hasPetition()}>
      <div class="flex items-center justify-between bg-gray-gradient p-2">
        <span class="text-sm">Petitions: {petition().count}</span>
        <button class="bg-blue-gradient px-3 py-1 text-xs" disabled={busy() || !props.report.isLocked || petition().userSigned} onClick={() => void petitionFor()}>
          {petition().userSigned ? "Petition signed" : "Sign petition"}
        </button>
      </div>
      </Show>
    </section>
  );
}

function signatureLabel(type: string): string {
  return ({
    cop: "Officer",
    copCommand: "Commanding Officer",
    judge: "Judge",
    marshall: "Marshall",
    marshallCommand: "Commanding Officer (Marshall)",
  } as Record<string, string>)[type] ?? type;
}
